/**
 * Capture UI context from a DOM element for feedback/report (DOM path, position, React component, HTML summary).
 * Used when user opens the report dialog so developers can reproduce the context.
 */

import type { UiContext } from '~/server/src/dtos';

const MAX_HTML_SNIPPET_LENGTH = 300;

/**
 * Build a short, readable DOM path (e.g. div[2] > div#radix-:rih: > …) by walking up from the element.
 */
function buildDomPath(element: Element): string {
  const segments: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.body) {
    const currentTag = current.tagName;
    let segment = currentTag.toLowerCase();
    if (current.id) {
      segment += '#' + current.id;
    } else if (current.className && typeof current.className === 'string') {
      const classes = current.className.trim().split(/\s+/).filter(Boolean);
      if (classes.length > 0) {
        segment += '.' + classes.slice(0, 2).join('.');
      }
    }
    if (!current.id && current.parentElement) {
      const siblings = Array.from(current.parentElement.children).filter(
        (el) => el.tagName === currentTag,
      );
      if (siblings.length > 1) {
        const index = siblings.indexOf(current) + 1;
        segment += `[${index}]`;
      }
    }
    segments.unshift(segment);
    current = current.parentElement;
  }

  return segments.join(' > ');
}

/**
 * Get React component name from data-component on the element or an ancestor.
 */
function getReactComponent(element: Element): string | undefined {
  let current: Element | null = element;
  while (current && current !== document.body) {
    const name = current.getAttribute?.('data-component');
    if (name) return name;
    current = current.parentElement;
  }
  return undefined;
}

/**
 * Build a short HTML element summary (tagName, id, className, truncated outerHTML).
 */
function getHtmlElementSummary(element: Element): string {
  const tag = element.tagName.toLowerCase();
  const id = element.id ? ' id="' + element.id + '"' : '';
  const className =
    element.className && typeof element.className === 'string'
      ? ' class="' + String(element.className).slice(0, 80) + '"'
      : '';
  const role = element.getAttribute?.('role') ? ' role="' + element.getAttribute('role') + '"' : '';
  let snippet = '<' + tag + id + className + role + '>';
  try {
    const outer = element.outerHTML;
    if (outer && outer.length <= MAX_HTML_SNIPPET_LENGTH) snippet = outer;
    else if (outer) snippet = outer.slice(0, MAX_HTML_SNIPPET_LENGTH) + '…';
  } catch {
    // ignore
  }
  return snippet;
}

/**
 * Capture UI context from the given element (e.g. the menu item that opened the report).
 * Returns null if element is null or not a DOM element, so callers can omit uiContext when absent.
 */
export function captureUiContext(element: Element | null): UiContext | null {
  if (!element || !(element instanceof Element)) return null;

  try {
    const rect = element.getBoundingClientRect();
    const position = {
      top: Math.round(rect.top),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
    const reactComponent = getReactComponent(element);
    const htmlElement = getHtmlElementSummary(element);
    const domPath = buildDomPath(element);

    return {
      domPath,
      position,
      reactComponent: reactComponent ?? undefined,
      htmlElement,
    };
  } catch {
    return null;
  }
}
